import { useEffect, useEffectEvent, useState } from 'react';

/**
 * Load a value for `key` (usually an id) and report it — `fallback` while it loads, if the load
 * fails, or when there is no key — plus whether the first load for this key is still running.
 *
 * The result is stored together with the key it was loaded for, so switching keys never shows the
 * previous key's value while the new one loads (e.g. the last organization's role after an org
 * switch), and nothing has to be reset with a setState inside an effect. `load` may change on every
 * render; only a new `key` starts a new load — put everything the load depends on into the key.
 */
export function useKeyedAsyncState<T>(
  key: string | null | undefined,
  load: (key: string) => Promise<T>,
  fallback: T,
): { value: T; loading: boolean } {
  const [result, setResult] = useState<{ key: string; ok: boolean; value?: T } | null>(null);
  const runLoad = useEffectEvent((k: string) => load(k));

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    runLoad(key)
      .then((value) => { if (!cancelled) setResult({ key, ok: true, value }); })
      .catch(() => { if (!cancelled) setResult({ key, ok: false }); });
    return () => { cancelled = true; };
  }, [key]);

  const current = key && result?.key === key ? result : null;
  return {
    value: current?.ok ? (current.value as T) : fallback,
    loading: Boolean(key) && !current,
  };
}

/** {@link useKeyedAsyncState}, value only. */
export function useKeyedAsync<T>(
  key: string | null | undefined,
  load: (key: string) => Promise<T>,
  fallback: T,
): T {
  return useKeyedAsyncState(key, load, fallback).value;
}
